import React from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@roger/ui-components';
import { Plus, X } from 'lucide-react';
import AttachmentUploadCard from './AttachmentUploadCard';
import type { ServiceLinesFormProps } from '../types';

const revenueCodeOptions = [
  { value: '0110', label: '0110 - Room and Board - Private' },
  { value: '0120', label: '0120 - Room and Board - Semi-Private Two Bed' },
  {
    value: '0130',
    label: '0130 - Room and Board - Semi-Private Three and Four Bed',
  },
  { value: '0140', label: '0140 - Room and Board - Private (Deluxe)' },
  { value: '0150', label: '0150 - Room and Board - Ward' },
  { value: '0160', label: '0160 - Room and Board - Other' },
  { value: '0200', label: '0200 - Intensive Care - General' },
  { value: '0210', label: '0210 - Intensive Care - Coronary Care' },
  { value: '0220', label: '0220 - Intensive Care - Pediatric' },
  { value: '0230', label: '0230 - Intensive Care - Psychiatric' },
  { value: '0240', label: '0240 - Intensive Care - Intermediate' },
  { value: '0250', label: '0250 - Intensive Care - Burn Care' },
  { value: '0260', label: '0260 - Intensive Care - Trauma' },
  { value: '0300', label: '0300 - Laboratory - General' },
  { value: '0310', label: '0310 - Laboratory - Chemistry' },
  { value: '0320', label: '0320 - Laboratory - Immunology' },
  { value: '0330', label: '0330 - Laboratory - Renal Patient (Home)' },
  { value: '0340', label: '0340 - Laboratory - Non-routine Dialysis' },
  { value: '0350', label: '0350 - Laboratory - Histology' },
  { value: '0360', label: '0360 - Laboratory - Cytology' },
  { value: '0370', label: '0370 - Laboratory - Other' },
];

const ServiceLinesForm: React.FC<ServiceLinesFormProps> = ({
  serviceLines,
  onServiceLineChange,
  onInstitutionalServiceChange,
  onAddServiceLine,
  onRemoveServiceLine,
  attachments,
  onAttachmentUpload,
  onAttachmentRemove,
  selectedPatientId,
}) => {
  // Helper to get attachments for a specific service line
  // Note: serviceLineNumber is 1-based, serviceLineIndex is 0-based
  const getServiceLineAttachments = (serviceLineIndex: number) =>
    attachments
      ? Array.from(attachments.values()).filter(
          a => a.serviceLineNumber === serviceLineIndex + 1
        )
      : [];
  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Service Lines</h3>
        <Button
          type='button'
          onClick={onAddServiceLine}
          size='sm'
          variant='outline'
        >
          <Plus className='w-4 h-4' />
          Add Service Line
        </Button>
      </div>

      {serviceLines.map((serviceLine, index) => (
        <Accordion
          key={index}
          type='single'
          collapsible
          className='border rounded-lg'
        >
          <AccordionItem value={`service-line-${index}`}>
            <AccordionTrigger className='px-4'>
              <div className='flex items-center justify-between w-full'>
                <span>
                  Service Line #{index + 1} -{' '}
                  {serviceLine.institutionalService?.procedureCode ||
                    'No Procedure Code'}
                </span>
                <Button
                  type='button'
                  onClick={e => {
                    e.stopPropagation();
                    onRemoveServiceLine(index);
                  }}
                  size='sm'
                  variant='outline'
                  className='ml-2'
                >
                  <X className='w-4 h-4' />
                </Button>
              </div>
            </AccordionTrigger>
            <AccordionContent className='px-4 pb-4'>
              <div className='space-y-4'>
                {/* Service Line Control Number and Description */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Line Item Control Number
                    </label>
                    <Input
                      value={serviceLine.lineItemControlNumber || ''}
                      onChange={e =>
                        onServiceLineChange(
                          index,
                          'lineItemControlNumber',
                          e.target.value
                        )
                      }
                      placeholder='Service line identifier'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Description
                    </label>
                    <Input
                      value={serviceLine.description || ''}
                      onChange={e =>
                        onServiceLineChange(
                          index,
                          'description',
                          e.target.value
                        )
                      }
                      placeholder='Service description'
                    />
                  </div>
                </div>

                {/* Institutional Service Required Fields */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Line Item Charge Amount{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <Input
                      type='number'
                      step='0.01'
                      value={
                        serviceLine.institutionalService
                          ?.lineItemChargeAmount || ''
                      }
                      onChange={e =>
                        onInstitutionalServiceChange(
                          index,
                          'lineItemChargeAmount',
                          e.target.value
                        )
                      }
                      placeholder='Charge amount'
                      required
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Service Unit Count <span className='text-red-500'>*</span>
                    </label>
                    <Input
                      type='number'
                      value={
                        serviceLine.institutionalService?.serviceUnitCount || ''
                      }
                      onChange={e =>
                        onInstitutionalServiceChange(
                          index,
                          'serviceUnitCount',
                          e.target.value
                        )
                      }
                      placeholder='Number of units'
                      required
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium mb-1'>
                      Measurement Unit <span className='text-red-500'>*</span>
                    </label>
                    <Input
                      value={
                        serviceLine.institutionalService?.measurementUnit || ''
                      }
                      onChange={e =>
                        onInstitutionalServiceChange(
                          index,
                          'measurementUnit',
                          e.target.value
                        )
                      }
                      placeholder='Unit (e.g., UN, MJ, DA)'
                      required
                    />
                  </div>
                </div>

                {/* Institutional Service Information */}
                <div>
                  <h4 className='text-sm font-semibold mb-3'>
                    Institutional Service Details
                  </h4>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Revenue Code
                      </label>
                      <Select
                        value={
                          serviceLine.institutionalService
                            ?.serviceLineRevenueCode || ''
                        }
                        onValueChange={value =>
                          onInstitutionalServiceChange(
                            index,
                            'serviceLineRevenueCode',
                            value
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select revenue code' />
                        </SelectTrigger>
                        <SelectContent>
                          {revenueCodeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Procedure Code
                      </label>
                      <Input
                        value={
                          serviceLine.institutionalService?.procedureCode || ''
                        }
                        onChange={e =>
                          onInstitutionalServiceChange(
                            index,
                            'procedureCode',
                            e.target.value
                          )
                        }
                        placeholder='CPT/HCPCS procedure code'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Procedure Identifier
                      </label>
                      <Input
                        value={
                          serviceLine.institutionalService
                            ?.procedureIdentifier || ''
                        }
                        onChange={e =>
                          onInstitutionalServiceChange(
                            index,
                            'procedureIdentifier',
                            e.target.value
                          )
                        }
                        placeholder='Procedure code type'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Procedure Description
                      </label>
                      <Input
                        value={
                          serviceLine.institutionalService?.description || ''
                        }
                        onChange={e =>
                          onInstitutionalServiceChange(
                            index,
                            'description',
                            e.target.value
                          )
                        }
                        placeholder='Procedure description'
                      />
                    </div>
                  </div>

                  {/* Procedure Modifiers */}
                  <div className='mt-4'>
                    <label className='block text-sm font-medium mb-2'>
                      Procedure Modifiers
                    </label>
                    <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                      {[0, 1, 2, 3].map(modIndex => (
                        <div key={modIndex}>
                          <Input
                            value={
                              serviceLine.institutionalService
                                ?.procedureModifiers?.[modIndex] || ''
                            }
                            onChange={e => {
                              const currentModifiers =
                                serviceLine.institutionalService
                                  ?.procedureModifiers || [];
                              const newModifiers = [...currentModifiers];
                              newModifiers[modIndex] = e.target.value;
                              onInstitutionalServiceChange(
                                index,
                                'procedureModifiers',
                                newModifiers
                              );
                            }}
                            placeholder={`Modifier ${modIndex + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>
                        Non-Covered Charge Amount
                      </label>
                      <Input
                        type='number'
                        step='0.01'
                        value={
                          serviceLine.institutionalService
                            ?.nonCoveredChargeAmount || ''
                        }
                        onChange={e =>
                          onInstitutionalServiceChange(
                            index,
                            'nonCoveredChargeAmount',
                            e.target.value
                          )
                        }
                        placeholder='Non-covered amount'
                      />
                    </div>
                  </div>

                  {/* Service Line Attachments */}
                  {selectedPatientId &&
                    onAttachmentUpload &&
                    onAttachmentRemove && (
                      <div className='mt-6'>
                        <label className='block text-sm font-medium mb-2'>
                          Service Line Attachments
                          {getServiceLineAttachments(index).length > 0 && (
                            <span className='ml-2 text-xs font-normal text-gray-500'>
                              ({getServiceLineAttachments(index).length} file
                              {getServiceLineAttachments(index).length !== 1
                                ? 's'
                                : ''}
                              )
                            </span>
                          )}
                        </label>
                        <div className='space-y-2'>
                          <p className='text-xs text-gray-600'>
                            Upload documentation specific to this service line.
                          </p>

                          {/* Display existing attachments for this service line */}
                          {getServiceLineAttachments(index).map(attachment => (
                            <AttachmentUploadCard
                              key={attachment.id}
                              patientId={selectedPatientId}
                              serviceLineNumber={index + 1}
                              existingAttachment={attachment}
                              onUploadSuccess={() => {}}
                              onRemove={() => onAttachmentRemove(attachment.id)}
                            />
                          ))}

                          {/* Add new attachment button - limit to 5 per service line */}
                          {getServiceLineAttachments(index).length < 5 && (
                            <AttachmentUploadCard
                              patientId={selectedPatientId}
                              serviceLineNumber={index + 1}
                              onUploadSuccess={attachment =>
                                onAttachmentUpload(attachment, index + 1)
                              }
                            />
                          )}

                          {getServiceLineAttachments(index).length >= 5 && (
                            <p className='text-xs text-gray-500 italic'>
                              Maximum of 5 attachments reached for this service
                              line.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ))}

      {serviceLines.length === 0 && (
        <div className='text-center py-8 text-gray-500'>
          No service lines added. Click "Add Service Line" to get started.
        </div>
      )}
    </div>
  );
};

export default ServiceLinesForm;
